import React from "react";

import { useAuth } from "@/context/auth";
import {
  acceptOwnerTerms,
  completeOwnerOnboarding,
  useOwnerOnboardingDraft,
  useSaveOwnerOnboarding,
} from "@/queries/api/ownerOnboardingApi";
import type {
  OwnerOnboardingCompletionResponse,
  OwnerOnboardingProfileRequest,
  TermsAcceptanceRequest,
} from "@/schemas/ownerOnboarding";
import type { OwnerOnboardingData } from "@/constants/ownerOnboarding";

const EMPTY: OwnerOnboardingData = {
  restaurantName: "",
  restaurantType: "",
  websiteUrl: "",
  ownerRole: "",
  ownerName: "",
  phone: "",
  termsAccepted: false,
  billingConfigured: false,
};

type OwnerOnboardingContextValue = {
  data: OwnerOnboardingData;
  patch: (p: Partial<OwnerOnboardingData>) => void;
  setRestaurantImageUrl: (url: string | undefined) => void;
  setOwnerPhotoUrl: (url: string | undefined) => void;
  saveDraft: () => Promise<void>;
  acceptTerms: (version: string) => Promise<void>;
  complete: () => Promise<OwnerOnboardingCompletionResponse>;
  isLoading: boolean;
  isSaving: boolean;
};

const Ctx = React.createContext<OwnerOnboardingContextValue | undefined>(
  undefined,
);

export function useOwnerOnboarding() {
  const ctx = React.useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useOwnerOnboarding must be used within OwnerOnboardingProvider",
    );
  }
  return ctx;
}

export function OwnerOnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [data, setData] = React.useState<OwnerOnboardingData>(EMPTY);
  const auth = useAuth();
  const draftQuery = useOwnerOnboardingDraft();
  const saveMutation = useSaveOwnerOnboarding();

  // Pre-fill name + phone from the Keycloak JWT so they're available
  // regardless of which step the user is on. (The About-You step locks
  // these as read-only; saveDraft on any step shouldn't send a partial
  // contact that fails BE @NotBlank validation.)
  React.useEffect(() => {
    const name = [auth.user?.firstName, auth.user?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const phone = auth.user?.phone ?? "";
    setData((prev) => ({
      ...prev,
      ownerName: prev.ownerName || name,
      phone: prev.phone || phone,
    }));
  }, [auth.user]);

  React.useEffect(() => {
    const draft = draftQuery.data;
    if (!draft) return;
    const draftAddress = draft.restaurant?.address;
    setData((prev) => ({
      ...prev,
      restaurantName: draft.restaurant?.name ?? prev.restaurantName,
      restaurantType: draft.restaurant?.type ?? prev.restaurantType,
      restaurantImageUrl: draft.restaurant?.imageUrl ?? prev.restaurantImageUrl,
      restaurantAddress: draftAddress?.formattedAddress
        ? {
            formattedAddress: draftAddress.formattedAddress,
            address1: draftAddress.address1 ?? undefined,
            city: draftAddress.city ?? undefined,
            state: draftAddress.state ?? undefined,
            zipcode: draftAddress.zipcode ?? undefined,
            latitude: draftAddress.latitude ?? undefined,
            longitude: draftAddress.longitude ?? undefined,
          }
        : prev.restaurantAddress,
      websiteUrl: draft.restaurant?.websiteUrl ?? prev.websiteUrl,
      ownerRole: draft.contact?.role ?? prev.ownerRole,
      ownerName: draft.contact?.name ?? prev.ownerName,
      phone: draft.contact?.phone ?? prev.phone,
      ownerPhotoUrl: draft.contact?.photoUrl ?? prev.ownerPhotoUrl,
      termsAccepted: draft.termsAccepted,
      termsVersion: draft.termsVersion ?? prev.termsVersion,
      billingConfigured: draft.billing.configured,
      cardBrand: draft.billing.cardBrand ?? prev.cardBrand,
      cardLast4: draft.billing.cardLast4 ?? prev.cardLast4,
    }));
  }, [draftQuery.data]);

  const patch = React.useCallback(
    (p: Partial<OwnerOnboardingData>) => setData((d) => ({ ...d, ...p })),
    [],
  );

  const setRestaurantImageUrl = React.useCallback(
    (url: string | undefined) => setData((d) => ({ ...d, restaurantImageUrl: url })),
    [],
  );
  const setOwnerPhotoUrl = React.useCallback(
    (url: string | undefined) => setData((d) => ({ ...d, ownerPhotoUrl: url })),
    [],
  );

  const toRequest = React.useCallback(
    (): OwnerOnboardingProfileRequest => ({
      // Only send restaurant when ALL @NotBlank fields are present -- name,
      // type, and address.formattedAddress all have @NotBlank on the BE, so a
      // partial restaurant (e.g. just name set from an early keystroke) would
      // fail Jakarta validation with 400.
      restaurant:
        data.restaurantName.trim() &&
        data.restaurantType.trim() &&
        data.restaurantAddress?.formattedAddress.trim()
          ? {
              name: data.restaurantName.trim(),
              type: data.restaurantType.trim(),
              websiteUrl: data.websiteUrl.trim() || undefined,
              address: {
                formattedAddress: data.restaurantAddress.formattedAddress.trim(),
                address1: data.restaurantAddress.address1,
                city: data.restaurantAddress.city,
                state: data.restaurantAddress.state,
                zipcode: data.restaurantAddress.zipcode,
                latitude: data.restaurantAddress.latitude,
                longitude: data.restaurantAddress.longitude,
              },
            }
          : undefined,
      // Only send contact when ALL required fields are present -- the BE
      // has @NotBlank on each, so a partial contact (e.g. just role from a
      // hydrated draft) would fail Jakarta validation with 400.
      contact:
        data.ownerRole.trim() && data.ownerName.trim() && data.phone.trim()
          ? {
              role: data.ownerRole.trim(),
              name: data.ownerName.trim(),
              phone: data.phone.trim(),
            }
          : undefined,
    }),
    [data],
  );

  const saveDraft = React.useCallback(async () => {
    await saveMutation.mutateAsync(toRequest());
  }, [saveMutation, toRequest]);

  const acceptTerms = React.useCallback(
    async (version: string) => {
      const body: TermsAcceptanceRequest = { accepted: true, version };
      const updated = await acceptOwnerTerms(body);
      setData((d) => ({
        ...d,
        termsAccepted: updated.termsAccepted,
        termsVersion: updated.termsVersion ?? version,
      }));
    },
    [],
  );

  const complete = React.useCallback(async () => {
    return await completeOwnerOnboarding();
  }, []);

  const value: OwnerOnboardingContextValue = React.useMemo(
    () => ({
      data,
      patch,
      setRestaurantImageUrl,
      setOwnerPhotoUrl,
      saveDraft,
      acceptTerms,
      complete,
      isLoading: draftQuery.isLoading,
      isSaving: saveMutation.isPending,
    }),
    [
      data,
      patch,
      setRestaurantImageUrl,
      setOwnerPhotoUrl,
      saveDraft,
      acceptTerms,
      complete,
      draftQuery.isLoading,
      saveMutation.isPending,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
